import React, { useState } from "react";
import SeoHead from "../components/SEO/SeoHead";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  CardActionArea,
  Chip,
  Tab,
  Tabs,
} from "@mui/material";
import { motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import portfolioData from "../data/portfolio.json";

interface Project {
  id: string;
  title: string;
  description: string;
  image: string;
  technologies: string[];
  implementation: string;
  challenges: string;
  results: string;
  type: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`portfolio-tabpanel-${index}`}
      aria-labelledby={`portfolio-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Portfolio: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  // Get unique types from portfolio data using Array.from for TypeScript compatibility
  const projectTypes = Array.from(
    new Set((portfolioData as Project[]).map((project) => project.type))
  );
  const categories = ["Alle", ...projectTypes];

  const reversedPortfolioData = [...portfolioData].reverse(); // Array umkehren

  const filteredProjects =
    activeTab === 0
      ? reversedPortfolioData // Verwende das umgekehrte Array
      : reversedPortfolioData.filter(
          (project) => project.type === categories[activeTab]
        );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        minHeight: "100vh",
        backgroundColor:
          theme.palette.mode === "dark" ? "background.default" : "grey.50",
      }}
    >
      <SeoHead 
        title="Portfolio | Website Referenzen & Projekte aus Darmstadt | pyzon"
        description="Entdecken Sie unsere Website-Referenzen und erfolgreichen Projekte aus Darmstadt und Umgebung. Sehen Sie, wie wir maßgeschneiderte Websites für verschiedene Branchen erstellen."
        keywords="Website Referenzen, Website Projekte Darmstadt, Website Beispiele, Webentwicklung Portfolio, pyzon Projekte, Website Showcase"
        canonicalPath="/portfolio"
      />
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.5rem" },
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              textAlign: "center",
            }}
          >
            Portfolio
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ maxWidth: "800px", mx: "auto", mb: 6, textAlign: "center" }}
          >
            Entdecken Sie unsere erfolgreich umgesetzten Projekte
          </Typography>
        </motion.div>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {categories.map((category, index) => (
              <Tab
                key={category}
                label={category}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  minWidth: 120,
                }}
              />
            ))}
          </Tabs>
        </Box>

        {categories.map((category, index) => (
          <TabPanel key={category} value={activeTab} index={index}>
            <Grid container spacing={4}>
              {filteredProjects.map((project, projectIndex) => (
                <Grid item xs={12} sm={6} md={4} key={project.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: projectIndex * 0.1 }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        background:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(255, 255, 255, 0.8)",
                        backdropFilter: "blur(10px)",
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "all 0.3s ease-in-out",
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow:
                            theme.palette.mode === "dark"
                              ? "0 8px 24px rgba(0,0,0,0.2)"
                              : "0 8px 24px rgba(0,0,0,0.1)",
                          "& .project-image": {
                            transform: "scale(1.05)",
                          },
                        },
                      }}
                    >
                      <CardActionArea
                        component={RouterLink}
                        to={`/portfolio/${project.id}`}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                        }}
                      >
                        <Box sx={{ position: "relative", paddingTop: "60%" }}>
                          <CardMedia
                            component="img"
                            image={project.image}
                            alt={project.title}
                            className="project-image"
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              transition: "transform 0.3s ease-in-out",
                            }}
                          />
                        </Box>
                        <CardContent
                          sx={{
                            flexGrow: 1,
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                              }}
                            >
                              {project.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 2,
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {project.description}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mt: "auto",
                            }}
                          >
                            {project.technologies.slice(0, 3).map((tech) => (
                              <Chip
                                key={tech}
                                label={tech}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: "primary.main",
                                  color: "primary.main",
                                }}
                              />
                            ))}
                            {project.technologies.length > 3 && (
                              <Chip
                                label={`+${project.technologies.length - 3}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderColor: "primary.main",
                                  color: "primary.main",
                                }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </TabPanel>
        ))}
      </Container>
    </Box>
  );
};

export default Portfolio;
